import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Badge,
  Button,
  Form,
  Alert,
  ProgressBar,
  InputGroup,
  Spinner
} from 'react-bootstrap';
import {
  FaServer,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSync,
  FaTerminal,
  FaPaperPlane,
  FaDatabase,
  FaClock,
  FaNetworkWired,
  FaChartLine,
  FaCloudUploadAlt,
  FaCloudDownloadAlt
} from 'react-icons/fa';
import axiosInstance from 'helpers/api';

interface OtisStatus {
  status: string;
  pid?: number;
  uptime?: string;
}

interface S3MonitorStatus {
  active: boolean;
  status: string;
}

interface CommandQueue {
  pending: number;
  processed: number;
  failed?: number;
}

interface S3Log {
  id: string;
  timestamp: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'command' | 'deployment';
  message: string;
  details?: any;
  server?: string;
}

interface ActiveOperation {
  id: string;
  type: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  timestamp: string;
  server: string;
  details: any;
}

interface S3ClaudeOrchestratorProps {
  wsConnection?: WebSocket | null;
}

const S3ClaudeOrchestrator: React.FC<S3ClaudeOrchestratorProps> = ({ wsConnection }) => {
  const [otisStatus, setOtisStatus] = useState<OtisStatus>({ status: 'checking' });
  const [s3MonitorStatus, setS3MonitorStatus] = useState<S3MonitorStatus>({ active: false, status: 'inactive' });
  const [commandQueue, setCommandQueue] = useState<CommandQueue>({ pending: 0, processed: 0 });
  const [s3Logs, setS3Logs] = useState<S3Log[]>([]);
  const [activeOperations, setActiveOperations] = useState<ActiveOperation[]>([]);
  const [command, setCommand] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const logsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchDashboardData();
    
    if (autoRefresh) {
      const interval = setInterval(fetchDashboardData, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  useEffect(() => {
    // Auto-scroll logs to bottom
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [s3Logs]);

  const fetchDashboardData = async () => {
    try {
      const response = await axiosInstance.get('ai-operations/dashboard');
      
      if (response.data) {
        setOtisStatus(response.data.status?.otis || { status: 'unknown' });
        setS3MonitorStatus(response.data.status?.s3Monitor || { active: false, status: 'inactive' });
        setCommandQueue(response.data.status?.commandQueue || { pending: 0, processed: 0 });
        setS3Logs(response.data.recentLogs || []);
        setActiveOperations(response.data.activeOperations || []);
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch orchestrator data:', err);
      // If auth error, show a user-friendly message
      if (err.response?.status === 401) {
        setError('Please log in to view S3 Orchestrator data');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view this data');
      }
      // Don't set error for other routine fetch failures
    }
  };

  const startS3Monitor = async () => {
    setLoading(true);
    try {
      await axiosInstance.post('ai-operations/monitor/start');
      await fetchDashboardData();
      setError(null);
    } catch (err) {
      setError('Failed to start S3 monitoring');
    } finally {
      setLoading(false);
    }
  };

  const stopS3Monitor = async () => {
    setLoading(true);
    try {
      await axiosInstance.post('ai-operations/monitor/stop');
      await fetchDashboardData();
      setError(null);
    } catch (err) {
      setError('Failed to stop S3 monitoring');
    } finally {
      setLoading(false);
    }
  };

  const sendOtisCommand = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!command.trim()) return;

    setLoading(true);
    try {
      const response = await axiosInstance.post('ai-operations/otis/command', {
        command: command.trim()
      });

      // Add command to logs
      const newLog: S3Log = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        type: 'command',
        message: `Command sent: ${command}`,
        details: response.data
      };
      setS3Logs(prev => [...prev, newLog]);
      
      setCommand('');
      setError(null);
      await fetchDashboardData();
    } catch (err) {
      setError('Failed to send command to OTIS');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'online':
      case 'active':
      case 'running':
        return <Badge bg="success">Online</Badge>;
      case 'offline':
      case 'inactive':
      case 'stopped':
        return <Badge bg="danger">Offline</Badge>;
      case 'checking':
        return <Badge bg="secondary">Checking...</Badge>;
      default:
        return <Badge bg="warning">Unknown</Badge>;
    }
  };

  const getLogIcon = (type: string) => {
    switch (type) {
      case 'error':
        return <FaExclamationTriangle className="text-danger" />;
      case 'success':
        return <FaCheckCircle className="text-success" />;
      case 'warning':
        return <FaExclamationTriangle className="text-warning" />;
      case 'command':
        return <FaTerminal className="text-primary" />;
      case 'deployment':
        return <FaCloudUploadAlt className="text-info" />;
      default:
        return <FaServer className="text-secondary" />;
    }
  };

  const getOperationStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      case 'in_progress':
        return <Badge bg="primary">In Progress</Badge>;
      case 'failed':
        return <Badge bg="danger">Failed</Badge>;
      default:
        return <Badge bg="secondary">Pending</Badge>;
    }
  };

  return (
    <Row className="g-3">
      {/* Status Cards */}
      <Col xs={12}>
        <Row className="g-3">
          <Col md={4}>
            <Card>
              <Card.Header>
                <FaServer className="me-2" />
                OTIS Orchestrator
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Status:</span>
                  {getStatusBadge(otisStatus.status)}
                </div>
                {otisStatus.pid && (
                  <div className="text-muted small">PID: {otisStatus.pid}</div>
                )}
                {otisStatus.uptime && (
                  <div className="text-muted small">Uptime: {otisStatus.uptime}</div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card>
              <Card.Header>
                <FaDatabase className="me-2" />
                S3 Monitor
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>Status:</span>
                  {getStatusBadge(s3MonitorStatus.active ? 'active' : 'inactive')}
                </div>
                <div className="d-grid">
                  {s3MonitorStatus.active ? (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={stopS3Monitor}
                      disabled={loading}
                    >
                      {loading ? <Spinner size="sm" /> : 'Stop Monitoring'}
                    </Button>
                  ) : (
                    <Button
                      variant="success"
                      size="sm"
                      onClick={startS3Monitor}
                      disabled={loading}
                    >
                      {loading ? <Spinner size="sm" /> : 'Start Monitoring'}
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card>
              <Card.Header>
                <FaNetworkWired className="me-2" />
                Command Queue
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <div className="d-flex justify-content-between">
                    <span>Pending:</span>
                    <Badge bg="warning">{commandQueue.pending}</Badge>
                  </div>
                </div>
                <div className="mb-2">
                  <div className="d-flex justify-content-between">
                    <span>Processed:</span>
                    <Badge bg="success">{commandQueue.processed}</Badge>
                  </div>
                </div>
                {commandQueue.failed !== undefined && (
                  <div>
                    <div className="d-flex justify-content-between">
                      <span>Failed:</span>
                      <Badge bg="danger">{commandQueue.failed}</Badge>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Col>

      {/* Command Interface */}
      <Col xs={12}>
        <Card>
          <Card.Header>
            <FaTerminal className="me-2" />
            OTIS Command Interface
          </Card.Header>
          <Card.Body>
            <Form onSubmit={sendOtisCommand}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Enter OTIS command (e.g., status, deploy backend, check queue)"
                  value={command}
                  onChange={(e) => setCommand(e.target.value)}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || !command.trim()}
                >
                  <FaPaperPlane className="me-2" />
                  Send Command
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                Available commands: status, deploy [service], agents, logs, check queue, restart [agent]
              </Form.Text>
            </Form>
          </Card.Body>
        </Card>
      </Col>

      {/* Active Operations */}
      {activeOperations.length > 0 && (
        <Col xs={12}>
          <Card>
            <Card.Header>
              <FaChartLine className="me-2" />
              Active Operations
            </Card.Header>
            <Card.Body>
              <Table responsive hover size="sm">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Server</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {activeOperations.map((op) => (
                    <tr key={op.id}>
                      <td className="text-muted small">
                        <FaClock className="me-1" />
                        {new Date(op.timestamp).toLocaleTimeString()}
                      </td>
                      <td>
                        <Badge bg="secondary">{op.server}</Badge>
                      </td>
                      <td>{op.type}</td>
                      <td>{getOperationStatusBadge(op.status)}</td>
                      <td className="small text-truncate" style={{ maxWidth: '200px' }}>
                        {typeof op.details === 'string' ? op.details : JSON.stringify(op.details)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      )}

      {/* S3 Logs */}
      <Col xs={12}>
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <FaCloudDownloadAlt className="me-2" />
              S3 Orchestration Logs
            </div>
            <div>
              <Form.Check
                type="switch"
                id="auto-refresh"
                label="Auto-refresh"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                inline
              />
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={fetchDashboardData}
                disabled={loading}
              >
                <FaSync className={loading ? 'fa-spin' : ''} />
              </Button>
            </div>
          </Card.Header>
          <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            {s3Logs.length === 0 ? (
              <div className="text-center text-muted py-5">
                <FaServer size={48} className="mb-3" />
                <p>No logs available yet. Start S3 monitoring to see activity.</p>
              </div>
            ) : (
              <div className="log-viewer">
                {s3Logs.map((log) => (
                  <div key={log.id} className="log-entry d-flex align-items-start mb-2 p-2 border-bottom">
                    <div className="me-2">{getLogIcon(log.type)}</div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between">
                        <strong className="text-break">{log.message}</strong>
                        <small className="text-muted ms-2">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </small>
                      </div>
                      {log.server && (
                        <Badge bg="info" className="me-1">{log.server}</Badge>
                      )}
                      {log.details && (
                        <pre className="text-muted small mb-0 mt-1" style={{ fontSize: '0.8rem' }}>
                          {typeof log.details === 'string' ? log.details : JSON.stringify(log.details, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={logsEndRef} />
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default S3ClaudeOrchestrator;